import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Stack } from "@mui/material";
import { colors, borderRadiusPx } from "../../../../../theme";

/**
 * Computes combinations n choose k: C(n, k) = n! / (k! * (n - k)!)
 */
export const calculateCombinations = (n, k) => {
  const nNum = Number(n);
  const kNum = Number(k);
  if (!nNum || !kNum || kNum > nNum || nNum <= 0 || kNum <= 0) return null;
  let result = 1;
  for (let i = 1; i <= kNum; i++) {
    result = (result * (nNum - i + 1)) / i;
  }
  return Math.round(result);
};

const MegaMatrixPreview = ({ capacity = 20, pickNumbers = 4 }) => {
  const cap = Number(capacity) || 20;
  const picks = Number(pickNumbers) || 4;

  const combinations = useMemo(
    () => calculateCombinations(cap, picks),
    [cap, picks]
  );

  // Generate visual sample preview balls (up to 8 balls max to keep preview compact)
  const displayCount = Math.min(cap, 8);
  const sampleBalls = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= displayCount; i++) {
      arr.push({
        num: i,
        isSelected: i <= Math.min(picks, 3), // highlight first few as sample selection
      });
    }
    return arr;
  }, [displayCount, picks]);

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: colors.slate[50],
        borderRadius: borderRadiusPx.lg,
        border: `1px solid ${colors.slate[200]}`,
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.25 }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: borderRadiusPx.circle,
              bgcolor: colors.emerald[500],
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: colors.slate[700],
              fontWeight: 700,
              fontSize: "0.78rem",
            }}
          >
            Matrix: Pick {picks} of {cap}
          </Typography>
        </Stack>

        {combinations && (
          <Typography
            variant="caption"
            sx={{
              color: colors.slate[500],
              fontWeight: 600,
              fontSize: "0.72rem",
            }}
          >
            1 in {combinations.toLocaleString()} Odds
          </Typography>
        )}
      </Stack>

      {/* Mini Visual Number Matrix Balls */}
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="flex-start"
        flexWrap="wrap"
        useFlexGap
      >
        {sampleBalls.map((ball) => (
          <Box
            key={ball.num}
            sx={{
              width: 24,
              height: 24,
              borderRadius: borderRadiusPx.circle,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
              fontWeight: 700,
              bgcolor: ball.isSelected ? colors.emerald[600] : colors.neutral.white,
              color: ball.isSelected ? colors.neutral.white : colors.slate[700],
              border: `1px solid ${
                ball.isSelected ? colors.emerald[600] : colors.slate[300]
              }`,
              boxShadow: ball.isSelected
                ? "0 2px 6px rgba(16, 185, 129, 0.35)"
                : "none",
            }}
          >
            {ball.num}
          </Box>
        ))}

        {cap > displayCount && (
          <Typography
            variant="caption"
            sx={{
              color: colors.slate[400],
              fontWeight: 700,
              fontSize: "0.72rem",
              ml: 0.5,
            }}
          >
            +{cap - displayCount} more
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

MegaMatrixPreview.propTypes = {
  capacity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pickNumbers: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default MegaMatrixPreview;
